import React, { useEffect, useState } from "react";
import { fetchVocabApply, type VocabCard } from "../../../../api/peContent";
import { chassis } from "../shared/chassis";
import type { Lang, Tier } from "../../../../api/serving";
import { peT } from "../../../../i18n/peLesson";

/**
 * Vocab Apply cards — step 1 (Pre-training). Shows the chapter's student-facing
 * key words BEFORE the story. role='apply' only (launch cards are teacher-only,
 * per the §2 7/6 split). Engagement-based: the student reviews; "Mark complete
 * & continue" (owned by LessonSequence) advances. No scoring here.
 */
const VocabStep: React.FC<{ chapterId: string; tier: Tier; lang: Lang }> = ({
  chapterId,
  tier,
  lang,
}) => {
  const t = peT(lang);
  const [cards, setCards] = useState<VocabCard[]>([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    let cancelled = false;
    setLoading(true);
    fetchVocabApply(chapterId, tier, lang)
      .then((c) => { if (!cancelled) setCards(c); })
      .catch((e) => {
        // Fail-soft: an empty/failed read degrades to an empty state, never crashes.
        console.warn("[VocabStep] vocab load failed:", e);
        if (!cancelled) setCards([]);
      })
      .finally(() => { if (!cancelled) setLoading(false); });
    return () => { cancelled = true; };
  }, [chapterId, tier, lang]);

  if (loading) {
    return (
      <div className="rounded-xl bg-white border border-gray-200 p-8 text-center text-gray-500">
        {t.loadingWords}
      </div>
    );
  }

  if (cards.length === 0) {
    return (
      <div className="rounded-xl bg-white border border-gray-200 p-8 text-center">
        <h2 className="text-2xl font-bold text-gray-900">{t.vocabTitle}</h2>
        <p className="mt-2 text-gray-500">{t.noWords}</p>
      </div>
    );
  }

  return (
    <section>
      <header className="mb-4">
        <h2 className="text-2xl font-bold text-gray-900">{t.wordsToKnow}</h2>
        <p className="text-gray-600">{t.wordsBlurb}</p>
      </header>
      <div className="grid gap-5 sm:grid-cols-2 lg:grid-cols-3">
        {cards.map((c) => (
          <article
            key={c.id}
            className="rounded-xl bg-white border border-gray-200 overflow-hidden shadow-sm flex flex-col"
          >
            {c.imageUrl && (
              <img
                src={c.imageUrl}
                alt={c.word}
                className="w-full aspect-square object-cover bg-gray-100"
                loading="lazy"
              />
            )}
            <div className="p-4 flex flex-col gap-1.5">
              <h3
                className="text-xl font-bold first-letter:uppercase"
                style={{ color: chassis.accent }}
              >
                {c.word}
              </h3>
              {c.definition && <p className="text-gray-800">{c.definition}</p>}
              {c.example && (
                <p className="text-sm text-gray-500 italic">“{c.example}”</p>
              )}
            </div>
          </article>
        ))}
      </div>
    </section>
  );
};

export default VocabStep;
